import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import {
    moveMapElement as moveMapElementAction,
    endMoveMapElement as endMoveMapElementAction,
    removeFakeOutcome as removeFakeOutcomeAction,
    addFakeOutcomeToXY as addFakeOutcomeToXYAction,
    removeAllFakeOutcomes as removeAllFakeOutcomesAction,
    openOutcomeConfig as openOutcomeConfigAction,
} from '../../actions/reduxActions/mapElements';
import {
    moveGroupElement as moveGroupElementAction,
    resizeGroupElement as resizeGroupElementAction,
    endResizeGroupElement as endResizeGroupElementAction,
    endMoveGroupElement as endMoveGroupElementAction,
} from '../../actions/reduxActions/groupElements';
import { getElements } from '../../config/elements';
import { getByID, isNullOrEmpty } from '../../utils/core';
import { getElementStyles, MAP_ELEMENT_WIDTH, MAP_ELEMENT_HEIGHT, GROUP_ELEMENT_WIDTH, GROUP_ELEMENT_HEIGHT } from './elements/elementStyles';
import { addNotification as addNotificationAction } from '../../actions/reduxActions/notification';
import { notificationTypes, MAP_ELEMENT_TYPES, keyCodes } from '../../constants';
import { getParents, getParentOffset, snap, getElementsDimensions } from './utils';
import {
    groupSelectedElements as groupSelectedElementsAction,
    ungroup as ungroupAction,
    setDraggingData as setDraggingDataAction, 
    setDraggingOverGroupElementID as setDraggingOverGroupElementIDAction,
    setContextMenuData as setContextMenuDataAction,
} from '../../actions/reduxActions/graphEditor';
import GraphContextMenu from './GraphContextMenu';
import MemoizedSVGContent from './MemoizedSVGContent';
import Notes from './elements/map/notes/Notes';
import GraphActionMenu from './GraphActionMenu';

const initialState = {
    // Props to be passed to the drag element
    sidebarDragElementProps: null,
    // Whether the drag element is being dragged and should react to events
    sidebarDragElementDragging: false,
    // Whether the drag element should be shown
    // (when the config is open, the element is shown, yet dragging is false)
    sidebarDragElementVisible: false,
    // Svg viewBox for zooming/panning
    viewBox: { x: 0, y: 0, width: 0, height: 0 },
    isMouseOverGraph: false,
    // Saved zoom level
    zoom: 0.6,
};

export class Graph extends Component { 

    constructor(props) {
        super(props);

        this.state = initialState;

        // Svg graph ref
        this.graphElement = createRef(null);
    }

    componentDidMount() {

        const svg = this.graphElement.current;

        const viewBox = svg.viewBox.baseVal;
        viewBox.height = svg.clientHeight * this.state.zoom;
        viewBox.width = svg.clientWidth * this.state.zoom;
        this.setViewBox(viewBox);

        document.querySelectorAll('.sidebar-element').forEach(
            (element) => {
                const { name: mapElementType, flow: flowName } = element.dataset;

                element.addEventListener('mousedown', (event) => {
                    const offset = this.calculateSVGPositionOfMapElement(event, mapElementType);
                    this.setState({
                        sidebarDragElementProps: {
                            mapElementType,
                            flowName,
                            x: offset.x,
                            y: offset.y,
                        },
                        sidebarDragElementDragging: true,
                        sidebarDragElementVisible: true,
                    });
                });
            },
        );

        // Cannot set passive parameter when using react event listeners
        // Without this an error is thrown: 
        // Unable to preventDefault inside passive event listener
        svg.addEventListener('wheel', this.onWheel, { passive: false });
        window.addEventListener('keyup', this.onKeyUp);
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('mouseup', this.onDragEnd);
    }

    componentDidUpdate(prevProps) {
        if (!this.props.showBackdrop && prevProps.showBackdrop) {
            // If the parent tells us the backdrop doesn't exist anymore
            // Then the user must have cancelled it e.g. pressing ESC
            this.clearAllSideBarDragElements();
            this.props.removeAllFakeOutcomes({ type: 'pending' });
        }
    }

    componentWillUnmount() {
        this.graphElement.current.removeEventListener('wheel', this.onWheel);
        window.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('mouseup', this.onDragEnd);
    }

    onKeyDown = (event) => {
        if (event.keyCode === keyCodes.G && event.ctrlKey) {
            // prevent browser search for grouping/ungrouping
            event.preventDefault();
        }
    }

    onKeyUp = (event) => {
        const { hoveringMapElementId, selectedElementIds, mapElements, hoveringGroupElementId, groupElements, flowId, editingToken } = this.props;

        const mapElementId = hoveringMapElementId || selectedElementIds[0];
        const mapElement = mapElements.find(m => m.id === mapElementId);
        const groupElementId = hoveringGroupElementId || selectedElementIds[0];
        const groupElement = groupElements.find(g => g.id === groupElementId);

        // Prioritise hovering over selection
        const activeElement = hoveringMapElementId ? 'map' :
            hoveringGroupElementId ? 'group' :
                mapElementId && isNullOrEmpty(mapElement) === false ? 'map' :
                    groupElementId && isNullOrEmpty(groupElement) === false ? 'group' :
                        null;

        
        // The user has pressed the Delete key
        if (event.keyCode === keyCodes.DELETE) {

            // If the active element is a map element
            if (activeElement === 'map') {
                const { elementType, id } = mapElement;
                
                // If it's a start element
                if (elementType.toLowerCase() === MAP_ELEMENT_TYPES.start) {
                    // Then throw a helpful error and do nothing
                    this.props.addNotification({
                        type: notificationTypes.ERROR,
                        message: 'You cannot delete the Start element',
                    });
                    return;
                }

                // Show the deletion System Flow
                this.props.openConfig(
                    'Delete Map Element',
                    {
                        editingToken,
                        flowId,
                        id,
                        elementType,
                    },
                );
                return;
            }

            // If the active element is a group element
            if (activeElement === 'group') {
                const { id } = groupElement;
                
                // Show the deletion System Flow
                this.props.openConfig(
                    'Delete Group Element',
                    {
                        editingToken,
                        flowId,
                        id,
                    },
                );
            }

        }

        if (
            event.ctrlKey &&
            event.shiftKey === false &&
            event.keyCode === keyCodes.G &&
            selectedElementIds.length > 1
        ) {
            this.props.groupSelectedElements({ flowId, editingToken, ...this.getSelectedElementsDimensions().newGroup });
        } else if (
            event.ctrlKey &&
            event.shiftKey &&
            event.keyCode === keyCodes.G &&
            isNullOrEmpty(groupElement) === false &&
            groupElement.elementType === MAP_ELEMENT_TYPES.group
        ) {
            this.props.ungroup({ flowId, editingToken, selectedGroupElementId: groupElement.id });
        }

    }

    onWheel = (event) => {
        event.preventDefault();

        this.props.setContextMenuData(null);

        const scaleDelta = (event.wheelDelta * -0.001 + 1);

        this.zoomByAmount(scaleDelta, event.clientX, event.clientY);
    }

    onDragEnd = (event) => {

        if (this.state.sidebarDragElementDragging) {
            if (this.state.isMouseOverGraph) {
                const { groupElements } = this.props;
                const { mapElementType, flowName } = this.state.sidebarDragElementProps;
                const element = getByID(mapElementType, getElements());

                const graphPositionSnapped = this.calculateSVGPositionOfMapElement(event, mapElementType);

                const elementParentOffset = getParentOffset(getParents({ groupElementId: this.props.draggingOverGroupElementId }, groupElements));

                const inputs = {
                    id: '',
                    flowId: this.props.flowId,
                    editingToken: this.props.editingToken,
                    elementType: mapElementType,
                    x: graphPositionSnapped.x - elementParentOffset.x,
                    y: graphPositionSnapped.y - elementParentOffset.y,
                    groupElementId: this.props.draggingOverGroupElementId || '',
                };

                // If this is a group/swimlane, give the system flow/editor the correct width/height
                if (mapElementType.toLowerCase() === MAP_ELEMENT_TYPES.swimlane || mapElementType.toLowerCase() === MAP_ELEMENT_TYPES.group) {
                    inputs.width = GROUP_ELEMENT_WIDTH;
                    inputs.height = GROUP_ELEMENT_HEIGHT;
                }

                const callbacks = [{
                    type: 'done',
                    execute: this.clearAllSideBarDragElements,
                }];

                const configOptions = null;

                this.props.openConfig(flowName, inputs, callbacks, configOptions, null, element);

                this.setState({ sidebarDragElementDragging: false });
            } else {
                this.clearAllSideBarDragElements();
            }
            this.props.setDraggingData(null);
            this.props.setDraggingOverGroupElementID(null);
            return;
        }

        if (!this.props.dragging) {
            return;
        }

        const { editingToken, openConfig, flowId } = this.props;
        const { dragType, elementId, previousElementPosition, hasMovedEnough } = this.props.dragging;

        if (hasMovedEnough) {

            let snappedElementPosition;
            if (previousElementPosition) {
                snappedElementPosition = {
                    x: snap(previousElementPosition.x),
                    y: snap(previousElementPosition.y),
                };
            }
    
            switch (dragType) {
            case 'group':
                this.props.endMoveGroupElement({
                    id: elementId,
                    editingToken,
                    ...snappedElementPosition,
                });
                break;
            case 'outcome': {
                const inputs = {
                    editingToken,
                    flowId,
                    id: elementId,
                }; 
    
                // Open the outcome config panel
                this.props.openOutcomeConfig({
                    mapElementId: elementId,
                    config: {
                        flow: 'Outcome',
                        inputs,
                    },
                    openConfig,
                });
                // Remove the fake new outcome we drew every frame
                this.props.removeFakeOutcome({
                    mapElementId: elementId,
                    type: 'new',
                });
                break;
            }
            case 'resize':
                this.props.endResizeGroupElement({
                    elementId,
                    editingToken,
                    flowId,
                });
                break;
            case 'graph':
                break;
            default:
            case 'map':
                this.props.endMoveMapElement({
                    id: elementId,
                    editingToken,
                    ...snappedElementPosition,
                });
                break;
            }
        }

        this.props.setDraggingData(null);
        this.props.setDraggingOverGroupElementID(null);
    }

    onMove = (event) => {
        this.setState({ isMouseOverGraph: true });

        event.preventDefault();
        const { groupElements, mapElements, dragging, contextMenu } = this.props;
        const { dragType, elementId, previousElementPosition, previousMousePosition, handleSide, hasMovedEnough, initialMousePosition } = dragging || {};

        const currentMousePosition = {
            x: event.clientX,
            y: event.clientY,
        };

        // Move the element by the same amount that the mouse moved
        const calculateElementPosition = () => {
            // first calculate the mouse movement on the canvas
            const delta = {
                x: (currentMousePosition.x - previousMousePosition.x) * this.state.zoom,
                y: (currentMousePosition.y - previousMousePosition.y) * this.state.zoom,
            };

            // then calculate newElementPosition
            const newElementPosition = {
                x: previousElementPosition.x + delta.x,
                y: previousElementPosition.y + delta.y,
            };

            return newElementPosition;
        };

        const snapPosition = position => ({
            x: snap(position.x),
            y: snap(position.y),
        });

        const calculateElementBounds = (element) => {
            const snappedNewElementPosition = snapPosition(calculateElementPosition());
            const elementParentOffset = getParentOffset(getParents(element, groupElements));

            // Turn snapped new element position into bounds
            const snappedNewElementGlobalBounds = {
                x: snappedNewElementPosition.x + elementParentOffset.x,
                y: snappedNewElementPosition.y + elementParentOffset.y,
                width: MAP_ELEMENT_WIDTH,
                height: MAP_ELEMENT_HEIGHT,
            };

            return snappedNewElementGlobalBounds;
        };

        const calculateDraggingOverElementID = (snappedNewElementBounds) => {
            let draggingOverGroup = null;

            groupElements.filter(el => el.id !== elementId).forEach((groupElement) => {
                const parentOffset = getParentOffset(getParents(groupElement, groupElements));

                if (this.calculateIfBoundIsWithinAnotherBounds({
                    innerBound: snappedNewElementBounds,
                    outerBound: {
                        x: groupElement.x + parentOffset.x,
                        y: groupElement.y + parentOffset.y,
                        width: groupElement.width,
                        height: groupElement.height,
                    },
                })) {
                    draggingOverGroup = groupElement.id;
                }
            });

            this.props.setDraggingOverGroupElementID(draggingOverGroup);
        };

        // We only move an element if the user is holding down the left mouse button
        if (event.buttons !== 1) {
            return;
        }

        if (contextMenu) {
            this.props.setContextMenuData(null);
        }

        if (this.state.sidebarDragElementDragging) {
            const { mapElementType } = this.state.sidebarDragElementProps;
            const offset = this.calculateSVGPositionOfMapElement(event, mapElementType);

            const styles = getElementStyles(mapElementType);

            calculateDraggingOverElementID({
                x: offset.x,
                y: offset.y,
                ...(styles.mapElement || styles.groupElement),
            });

            this.setState({
                sidebarDragElementProps: {
                    ...this.state.sidebarDragElementProps,
                    x: offset.x,
                    y: offset.y,
                },
            });
            return;
        }

        const movement = {
            x: event.movementX * this.state.zoom,
            y: event.movementY * this.state.zoom,
        };

        if (dragging) {
            const newHasMovedEnough = hasMovedEnough ||
                Math.abs(currentMousePosition.x - initialMousePosition.x) > 5 ||
                Math.abs(currentMousePosition.y - initialMousePosition.y) > 5;

            let newElementPosition;

            switch (dragType) {
            case 'group':
                newElementPosition = calculateElementPosition();

                this.props.moveGroupElement({
                    elementId,
                    ...snapPosition(newElementPosition),
                    groupRects: [],
                });

                this.props.setDraggingData({
                    ...dragging,
                    dragType,
                    elementId,
                    previousElementPosition: newElementPosition,
                    previousMousePosition: currentMousePosition,
                    hasMovedEnough: newHasMovedEnough,
                });

                calculateDraggingOverElementID(calculateElementBounds(groupElements.find(el => el.id === elementId)));
                break;
            case 'outcome':
                this.props.addFakeOutcomeToXY({
                    fromMapElementId: elementId,
                    toXY: this.calculateSVGPositionFromXY(currentMousePosition),
                });

                this.props.setDraggingData({
                    ...dragging,
                    hasMovedEnough: newHasMovedEnough,
                });
                break;
            case 'resize': {
                const delta = {
                    x: (currentMousePosition.x - previousMousePosition.x) * this.state.zoom,
                    y: (currentMousePosition.y - previousMousePosition.y) * this.state.zoom,
                };

                this.props.setDraggingData({
                    ...dragging,
                    hasMovedEnough: newHasMovedEnough,
                });

                this.props.resizeGroupElement({
                    elementId,
                    side: handleSide,
                    delta,
                    currentMousePosition,
                });
                break;
            }
            case 'graph':
                break;
            default:
            case 'map':
                newElementPosition = calculateElementPosition();

                this.props.moveMapElement({
                    mapElementId: elementId,
                    ...snapPosition(newElementPosition),
                });

                this.props.setDraggingData({
                    ...dragging,
                    dragType,
                    elementId,
                    previousElementPosition: newElementPosition,
                    previousMousePosition: currentMousePosition,
                    hasMovedEnough: newHasMovedEnough,
                });

                calculateDraggingOverElementID(calculateElementBounds(mapElements.find(el => el.id === elementId)));
                break;
            }
            return;
        }

        this.setViewBox({
            x: this.state.viewBox.x - movement.x,
            y: this.state.viewBox.y - movement.y,
            width: this.state.viewBox.width,
            height: this.state.viewBox.height,
        });
    }

    getKeysWhere(predicate, obj) {
        return Object.entries(obj).reduce(
            (keys, [key, val]) => predicate(key, val) ? keys.concat(key) : keys,
            [],
        );
    }

    zoomByAmount = (amount, x, y) => {
        let zoomTarget = { x, y };

        if (isNullOrEmpty(x) || isNullOrEmpty(y)) {
            zoomTarget = this.calculateGraphCentre();
        }

        const startPoint = this.calculateSVGPositionFromXY(zoomTarget);

        this.setViewBox({
            x: this.state.viewBox.x - (startPoint.x - this.state.viewBox.x) * (amount - 1),
            y: this.state.viewBox.y - (startPoint.y - this.state.viewBox.y) * (amount - 1),
            width: this.state.viewBox.width * amount,
            height: this.state.viewBox.height * amount,
        });

        this.setZoom(this.state.zoom * amount);
    }

    setZoom = (zoom) => { this.setState({ zoom }); }

    setViewBox = (viewBox) => { this.setState({ viewBox }); }

    openContextMenu = (event) => {
        const { hoveringMapElementId, hoveringGroupElementId } = this.props;

        event.preventDefault();

        let activeElement = { id: null, type: 'graph' };

        if (hoveringGroupElementId) {
            activeElement = { id: hoveringGroupElementId, type: 'group' };
        }

        if (hoveringMapElementId) {
            activeElement = { id: hoveringMapElementId, type: 'map' };
        }

        this.props.setContextMenuData({
            show: true,
            x: event.clientX,
            y: event.clientY,
            activeElement,
        });
    }

    /**
     * Returns the x,y,width,height of a rectangle around all selected elements
     */
    getSelectedElementsDimensions() {
        const { mapElements, selectedElementIds, groupElements } = this.props;

        const { lowestX, highestX, lowestY, highestY } = getElementsDimensions(selectedElementIds, mapElements, groupElements)

        const selectionBoundaryMargin = 20;

        return {
            newGroup: {
                x: lowestX - selectionBoundaryMargin,
                y: lowestY - selectionBoundaryMargin,
                width: highestX - lowestX + (selectionBoundaryMargin * 2),
                height: highestY - lowestY + (selectionBoundaryMargin * 2),
            },
            makeGroupIcon: {
                x: highestX,
                y: lowestY - 5 - selectionBoundaryMargin,
                width: 20,
                height: 20,
            },
        };
    }

    clearAllSideBarDragElements = () => this.setState({
        sidebarDragElementVisible: false,
        sidebarDragElementDragging: false,
    });

    calculateGraphCentre = () => {
        const flowEditorRect = document.getElementsByClassName('flow-editor')[0].getBoundingClientRect();
        return {
            x: flowEditorRect.x + (flowEditorRect.width / 2),
            y: flowEditorRect.y + (flowEditorRect.height / 2),
        };
    }

    calculateIfBoundIsWithinAnotherBounds({
        innerBound: { x: boundsX, y: boundsY, width: boundsWidth, height: boundsHeight },
        outerBound,
    }) {
        // If all corners of the inside bound are within the outside bound,
        // ┌───────┐
        // │ ●──●  │
        // │ │  │  │
        // │ ●──●  │
        // └───────┘
        // then the inner object is completely within the outer bound
        if (
            // Top left corner check
            this.calculateIfPointIsWithinBounds({
                point: { x: boundsX, y: boundsY },
                bounds: outerBound,
            }) &&
            // Top right corner check
            this.calculateIfPointIsWithinBounds({
                point: { x: boundsX + boundsWidth, y: boundsY },
                bounds: outerBound,
            }) &&
            // Bottom left corner check
            this.calculateIfPointIsWithinBounds({
                point: { x: boundsX, y: boundsY + boundsHeight },

                bounds: outerBound,
            }) &&
            // Bottom right corner check
            this.calculateIfPointIsWithinBounds({
                point: { x: boundsX + boundsWidth, y: boundsY + boundsHeight },
                bounds: outerBound,
            })
        ) {
            return true;
        }
        return false;
    }

    calculateIfPointIsWithinBounds({
        point: { x: pointX, y: pointY },
        bounds: { x: boundsX, y: boundsY, width: boundsWidth, height: boundsHeight },
    }) {
        // If the point's x coordinate lies with the bound's horizontal area
        // │ ●     │
        // and the point's y coordinate lies with the bound's vertical area,
        //  ───────
        //   ●     
        //  ───────
        // then the point is within the bound
        // ┌───────┐
        // │ ●     │
        // └───────┘
        if (
            // Check if the point is inside the bounds horizontally
            pointX >= boundsX && pointX <= boundsX + boundsWidth &&
            // Check if the point is inside the bounds vertically
            pointY >= boundsY && pointY <= boundsY + boundsHeight
        ) {
            return true;
        }
        return false;
    }

    calculateSVGPositionFromXY({ x, y }) {
        const point = this.graphElement.current.createSVGPoint();
        point.x = x;
        point.y = y;
        return point.matrixTransform(this.graphElement.current.getScreenCTM().inverse());
    }

    calculateSVGPositionOfMapElement(event, mapElementType) {
        const elementStyle = getElementStyles(mapElementType);
        const { width, height } = elementStyle.mapElement ? elementStyle.mapElement : elementStyle.groupElement;

        const point = this.calculateSVGPositionFromXY({ x: event.clientX, y: event.clientY });
        point.x = snap(point.x - (width / 2));
        point.y = snap(point.y - (height / 2));
        return point;
    }

    render() {
        const {
            flowId,
            editingToken,
            mapElementIdFlowId = {},
            groupElementIdFlowId = {},
            mapElements,
            groupSelectedElements,
            selectedElementIds,
            openConfig,
            groupElements,
            setSelectedMapElement,
            showBackdrop,
            setContextMenuData,
        } = this.props;

        const { newGroup, makeGroupIcon } = this.getSelectedElementsDimensions();

        return (
            <div className="graph-canvas-wrapper">
                <div className={`backdrop${showBackdrop ? ' active' : ''}`} />
                <svg
                    className="graph-canvas"
                    onContextMenu={this.openContextMenu}
                    onMouseDown={() => setContextMenuData(null)}
                    onMouseMove={this.onMove}
                    onMouseOut={() => this.setState({ isMouseOverGraph: false })}
                    onBlur={() => this.setState({ isMouseOverGraph: false })}
                    ref={this.graphElement}
                    viewBox={`${this.state.viewBox.x} ${this.state.viewBox.y} ${this.state.viewBox.width} ${this.state.viewBox.height}`}
                >
                    <g>
                        <defs>
                            <pattern id={`dots-${flowId}`} patternUnits="userSpaceOnUse" width="10" height="10">
                                <circle cx="0" cy="0" r="1" fill="#ddd" />
                                <circle cx="0" cy="10" r="1" fill="#ddd" />
                                <circle cx="10" cy="0" r="1" fill="#ddd" />
                                <circle cx="10" cy="10" r="1" fill="#ddd" />
                            </pattern>
                        </defs>
                        <rect fill={`url(#dots-${flowId})`} x={this.state.viewBox.x} y={this.state.viewBox.y} width="100%" height="100%" />
                        <MemoizedSVGContent
                            flowId={flowId}
                            editingToken={editingToken}
                            mapElementIdFlowId={mapElementIdFlowId || {}}
                            groupElementIdFlowId={groupElementIdFlowId || {}}
                            mapElements={mapElements}
                            groupSelectedElements={groupSelectedElements}
                            selectedElementIds={selectedElementIds}
                            openConfig={openConfig}
                            getKeysWhere={this.getKeysWhere}
                            sidebarDragElementVisible={this.state.sidebarDragElementVisible}
                            sidebarDragElementProps={this.state.sidebarDragElementProps}
                            newGroup={newGroup}
                            makeGroupIcon={makeGroupIcon}
                        />
                    </g>
                </svg>
                <GraphActionMenu 
                    getKeysWhere={this.getKeysWhere}
                    flowId={flowId}
                    mapElementIdFlowId={mapElementIdFlowId}
                    groupElementIdFlowId={groupElementIdFlowId}
                    mapElements={mapElements}
                    groupElements={groupElements}
                    graphElement={this.graphElement}
                    zoom={this.zoomByAmount}
                    setZoom={this.setZoom}
                    setViewBox={this.setViewBox}
                />
                <GraphContextMenu 
                    setSelectedMapElement={setSelectedMapElement}
                    openConfig={openConfig}
                    flowId={flowId}
                    editingToken={editingToken}
                />
                {this.graphElement.current && editingToken ? (
                    <Notes
                        editingToken={editingToken}
                        flowId={flowId}
                        canvasNode={this.graphElement.current.getBoundingClientRect()}
                    />
                ) : null}
            </div>
        );
    }
}

export default connect(
    ({ 
        mapElements,
        groupElements,
        mapElementIdFlowId,
        groupElementIdFlowId,
        graphEditor,
    }) => ({  
        mapElements,
        groupElements,
        mapElementIdFlowId,
        groupElementIdFlowId,
        ...graphEditor,
    }),
    {
        moveMapElement: moveMapElementAction,
        endMoveMapElement: endMoveMapElementAction,
        removeFakeOutcome: removeFakeOutcomeAction,
        addFakeOutcomeToXY: addFakeOutcomeToXYAction,
        removeAllFakeOutcomes: removeAllFakeOutcomesAction,
        addNotification: addNotificationAction,
        groupSelectedElements: groupSelectedElementsAction,
        ungroup: ungroupAction,
        setDraggingData: setDraggingDataAction,
        moveGroupElement: moveGroupElementAction,
        setDraggingOverGroupElementID: setDraggingOverGroupElementIDAction,
        openOutcomeConfig: openOutcomeConfigAction,
        resizeGroupElement: resizeGroupElementAction,
        endResizeGroupElement: endResizeGroupElementAction,
        endMoveGroupElement: endMoveGroupElementAction,
        setContextMenuData: setContextMenuDataAction,
    },
)(Graph);
